import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getApiUrl } from '@/lib/config';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Only connect if user is authenticated and has token
    if (!state.isAuthenticated || !state.token) {
      // Disconnect if previously connected
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Create socket connection with JWT authentication
    const socketUrl = getApiUrl();

    const newSocket = io(socketUrl, {
      auth: {
        token: state.token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Sem teto de tentativas (default Infinity). Com `reconnectionAttempts: 5`
      // o socket DESISTIA PARA SEMPRE depois de ~20s de rede ruim (notebook
      // dormindo, troca de Wi-Fi, VPN com perda) — a partir daí nenhum evento
      // chegava até recarregar a página, enquanto o contador seguia atualizando
      // pelo refetchInterval e o push pelo OneSignal. Incidente 2026-08-29; a
      // ressincronização ao voltar mora em `useSocketReconnectResync`.
    });

    // Connection event handlers
    newSocket.on('connect', () => {
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error.message);
      setIsConnected(false);
    });

    newSocket.on('error', (error) => {
      console.error('❌ Socket.IO error:', error);
    });

    setSocket(newSocket);

    // Cleanup function
    return () => {
      newSocket.disconnect();
    };
  }, [state.isAuthenticated, state.token]);

  const value: SocketContextType = {
    socket,
    isConnected
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
